import { useEffect } from 'react'
import { Bell, BellOff, Check, Trash2, X } from 'lucide-react'
import { useNotificationsStore } from '../store'
import { useAuthStore } from '../../auth/store'
import { NotificationItem } from './NotificationItem'
import { ChannelIndicator } from './ChannelIndicator'
import { Button } from '../../../shared/components/Button'
import { EmptyState } from '../../../shared/components/EmptyState'

interface NotificationCenterProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const { user } = useAuthStore()
  const {
    notifications,
    unreadCount,
    activeChannel,
    connectionState,
    isSubscribed,
    permission,
    isLoading,
    subscribe,
    unsubscribe,
    connectWebSocket,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    checkStatus,
  } = useNotificationsStore()

  // Connect WebSocket when panel opens
  useEffect(() => {
    if (isOpen && user?.careGroupId && user?.id) {
      connectWebSocket(user.careGroupId, user.id)
    }
  }, [isOpen, user?.careGroupId, user?.id, connectWebSocket])

  // Check push subscription status on mount
  useEffect(() => {
    if (isOpen) {
      checkStatus()
    }
  }, [isOpen, checkStatus])

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleTogglePush = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe()
      } else {
        await subscribe()
      }
    } catch {
      // Error is handled in store
    }
  }

  return (
    <>
      <div className="notification-backdrop" onClick={onClose} />
      <div className="notification-center">
        <div className="notification-center__header">
          <div className="notification-center__title">
            <Bell size={20} />
            <span>Notificaciones</span>
            {unreadCount > 0 && (
              <span className="notification-center__badge">{unreadCount}</span>
            )}
          </div>

          <div className="notification-center__actions">
            <ChannelIndicator channel={activeChannel} />
            <button className="notification-center__close" onClick={onClose} aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="notification-center__controls">
          <Button
            variant="ghost"
            size="sm"
            icon={isSubscribed ? BellOff : Bell}
            loading={isLoading}
            onClick={handleTogglePush}
            disabled={permission === 'denied'}
            title={
              permission === 'denied'
                ? 'Notificaciones bloqueadas en el navegador'
                : isSubscribed
                  ? 'Desactivar push'
                  : 'Activar push'
            }
          >
            {isSubscribed ? 'Push activo' : 'Activar push'}
          </Button>

          {notifications.length > 0 && (
            <>
              <Button variant="ghost" size="sm" icon={Check} onClick={markAllAsRead}>
                Marcar leídas
              </Button>
              <Button variant="ghost" size="sm" icon={Trash2} onClick={clearNotifications}>
                Limpiar
              </Button>
            </>
          )}
        </div>

        <div className="notification-center__content">
          {notifications.length === 0 ? (
            <EmptyState
              icon={<Bell size={48} />}
              title="Sin notificaciones"
              description="Las notificaciones de medicamentos y citas aparecerán aquí"
            />
          ) : (
            <div className="notification-center__list">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  id={notification.id}
                  type={notification.type}
                  title={notification.title}
                  body={notification.body}
                  timestamp={notification.timestamp}
                  read={notification.read}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          )}
        </div>

        <div className="notification-center__footer">
          <span className="notification-center__status">
            {connectionState === 'connected' && 'Conectado'}
            {connectionState === 'connecting' && 'Conectando...'}
            {connectionState === 'reconnecting' && 'Reconectando...'}
            {connectionState === 'disconnected' && 'Desconectado'}
          </span>
        </div>

        <style>{`
          .notification-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.3);
            z-index: 100;
          }

          .notification-center {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            max-width: 400px;
            background: var(--color-bg-primary);
            box-shadow: var(--shadow-lg);
            z-index: 101;
            display: flex;
            flex-direction: column;
            animation: slideIn 0.2s ease-out;
          }

          @keyframes slideIn {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }

          .notification-center__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: var(--spacing-4);
            border-bottom: 1px solid var(--color-border);
          }

          .notification-center__title {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            font-weight: 600;
            font-size: var(--font-size-lg);
          }

          .notification-center__badge {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 20px;
            height: 20px;
            padding: 0 var(--spacing-1);
            border-radius: var(--radius-full);
            background: var(--color-primary);
            color: white;
            font-size: var(--font-size-xs);
            font-weight: 600;
          }

          .notification-center__actions {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
          }

          .notification-center__close {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            padding: 0;
            border: none;
            border-radius: var(--radius-md);
            background: transparent;
            color: var(--color-text-secondary);
            cursor: pointer;
            transition: all 0.2s;
          }

          .notification-center__close:hover {
            background: var(--color-bg-secondary);
            color: var(--color-text-primary);
          }

          .notification-center__controls {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-4);
            border-bottom: 1px solid var(--color-border);
            flex-wrap: wrap;
          }

          .notification-center__content {
            flex: 1;
            overflow-y: auto;
            padding: var(--spacing-3);
          }

          .notification-center__list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
          }

          .notification-center__footer {
            padding: var(--spacing-2) var(--spacing-4);
            border-top: 1px solid var(--color-border);
            text-align: center;
          }

          .notification-center__status {
            font-size: var(--font-size-xs);
            color: var(--color-text-tertiary);
          }

          @media (max-width: 480px) {
            .notification-center {
              max-width: 100%;
            }
          }
        `}</style>
      </div>
    </>
  )
}
